import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ReactDOM from 'react-dom';
import {
    Badge,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Label,
    Table,
    Row,
} from 'reactstrap';
import IntlMessages from '../../../helpers/IntlMessages';
import { Colxx } from '../../../components/common/CustomBootstrap';
import { AddCommaNumber } from '../../../helpers/Utils';
import classnames from 'classnames';

const normalizeArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (value === undefined || value === null || value === '') {
        return [];
    }

    return [value];
};

const getOptionNameByIds = (selectedIds, options = []) => {
    const normalizedIds = normalizeArray(selectedIds).map((id) => `${id}`);
    if (!normalizedIds.length || !options.length) {
        return [];
    }

    return options
        .filter((option) => normalizedIds.includes(`${option.id}`))
        .map((option) => option.name || option.label || option.value)
        .filter(Boolean);
};

const getOptionByValue = (value, options = []) =>
    options.find((option) => `${option.value}` === `${value}`);

const formatPercentLabel = (value) => {
    const label = `${value}`;
    return label.includes('%') ? label : `${label}%`;
};

const normalizeDeliveryTimeValue = (value) => {
    const raw = `${value ?? ''}`.trim();
    if (!raw) {
        return '';
    }

    const validTimeMatch = raw.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (validTimeMatch) {
        return raw;
    }

    const amPmMatch = raw.match(/^(\d{1,2}):([0-5]\d)\s*([aApP])[mM]?$/);
    if (amPmMatch) {
        let hour = parseInt(amPmMatch[1], 10);
        const minute = amPmMatch[2];
        const meridiem = amPmMatch[3].toLowerCase();

        if (hour === 12) {
            hour = meridiem === 'a' ? 0 : 12;
        } else if (meridiem === 'p') {
            hour += 12;
        }

        return `${`${hour}`.padStart(2, '0')}:${minute}`;
    }

    const digits = raw.replace(/\D/g, '').slice(0, 4);
    if (!digits) {
        return '';
    }

    if (digits.length <= 2) {
        return digits;
    }

    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const isHundredPercent = (value) => {
    if (value === undefined || value === null || value === '') {
        return false;
    }

    const numericValue = parseFloat(`${value}`.replace('%', '').trim());
    return Number.isFinite(numericValue) && numericValue === 100;
};

const pickFirstNonEmptyArray = (...candidates) => {
    for (let i = 0; i < candidates.length; i++) {
        if (Array.isArray(candidates[i]) && candidates[i].length) {
            return candidates[i];
        }
    }

    return [];
};

const flattenMenuItems = (menuItems = []) =>
    menuItems.reduce((result, item) => {
        if (item && Array.isArray(item.data)) {
            return result.concat(item.data);
        }

        if (item && item.id !== undefined) {
            return result.concat(item);
        }

        return result;
    }, []);

const getMatchedProduct = (order, menuItems = []) => {
    const products = flattenMenuItems(menuItems);
    return products.find(
        (product) =>
            `${product.id}` === `${order.id}` ||
            `${product.id}` === `${order.product_id}`,
    );
};

const getOrderDetails = (order, menuItems = []) => {
    const matchedProduct = getMatchedProduct(order, menuItems);

    const sizeOptions = pickFirstNonEmptyArray(
        order.sizeSettings,
        order.sizeOptions,
        Array.isArray(order.size) ? order.size : null,
        matchedProduct?.size,
    );
    const toppingOptions = pickFirstNonEmptyArray(
        order.toppingSettings,
        order.toppingOptions,
        Array.isArray(order.topping) ? order.topping : null,
        matchedProduct?.topping,
    );
    const flavorOptions = pickFirstNonEmptyArray(
        order.flavorSettings,
        order.flavorOptions,
        Array.isArray(order.flavor) ? order.flavor : null,
        matchedProduct?.flavor,
    );
    const iceOptions = pickFirstNonEmptyArray(
        order.iceSettings,
        Array.isArray(order.ice) ? order.ice : null,
        matchedProduct?.ice,
    );
    const sugarOptions = pickFirstNonEmptyArray(
        order.sugarSettings,
        Array.isArray(order.sugar) ? order.sugar : null,
        matchedProduct?.sugar,
    );

    const sizeNames = getOptionNameByIds(order.sizeSelectedId, sizeOptions);
    const flavorNames = getOptionNameByIds(
        order.flavorSelectedId,
        flavorOptions,
    );
    const toppingNames = getOptionNameByIds(
        order.toppingSelectedId,
        toppingOptions,
    );

    const iceOption = getOptionByValue(order.iceSelectedValue, iceOptions);
    const sugarOption = getOptionByValue(order.sugarSelectedValue, sugarOptions);

    const hasIceValue =
        order.iceSelectedValue !== undefined &&
        order.iceSelectedValue !== null &&
        order.iceSelectedValue !== '' &&
        !isHundredPercent(order.iceSelectedValue);
    const hasSugarValue =
        order.sugarSelectedValue !== undefined &&
        order.sugarSelectedValue !== null &&
        order.sugarSelectedValue !== '' &&
        !isHundredPercent(order.sugarSelectedValue);
    const note = (order.note || '').trim();

    return {
        sizeNames,
        flavorNames,
        toppingNames,
        iceLabel: hasIceValue
            ? formatPercentLabel(
                  iceOption ? iceOption.name : order.iceSelectedValue,
              )
            : '',
        sugarLabel: hasSugarValue
            ? formatPercentLabel(
                  sugarOption ? sugarOption.name : order.sugarSelectedValue,
              )
            : '',
        note,
    };
};

const OrderDetailBadges = ({ order, menuItems = [] }) => {
    const { sizeNames, flavorNames, toppingNames, iceLabel, sugarLabel, note } =
        getOrderDetails(order, menuItems);

    const hasDetails =
        sizeNames.length ||
        flavorNames.length ||
        toppingNames.length ||
        Boolean(iceLabel) ||
        Boolean(sugarLabel) ||
        Boolean(note);

    if (!hasDetails) {
        return null;
    }

    return (
        <tr>
            <td colSpan='5' className='border-top-0 pt-0 pb-2 pl-4'>
                {sizeNames.map((sizeName, idx) => (
                    <Badge
                        key={`size-${sizeName}-${idx}`}
                        className='mr-1 mb-1'
                        color='primary'
                    >
                        {sizeName}
                    </Badge>
                ))}
                {flavorNames.map((flavorName, idx) => (
                    <Badge
                        key={`flavor-${flavorName}-${idx}`}
                        className='mr-1 mb-1'
                        color='info'
                    >
                        Vị: {flavorName}
                    </Badge>
                ))}
                {toppingNames.map((toppingName, idx) => (
                    <Badge
                        key={`topping-${toppingName}-${idx}`}
                        className='mr-1 mb-1'
                        color='success'
                    >
                        {toppingName}
                    </Badge>
                ))}
                {iceLabel ? (
                    <Badge className='mr-1 mb-1' color='warning'>
                        {`Đá: ${iceLabel}`}
                    </Badge>
                ) : null}
                {sugarLabel ? (
                    <Badge className='mr-1 mb-1' color='info'>
                        {`Đường: ${sugarLabel}`}
                    </Badge>
                ) : null}
                {note ? (
                    <Badge className='mr-1 mb-1' color='secondary'>
                        {`Ghi chú: ${note}`}
                    </Badge>
                ) : null}
            </td>
        </tr>
    );
};

const renderListOrders = ({
    orders,
    incrementItem,
    decrementItem,
    onRemoveOrder,
    menuItems,
}) => {
    return (
        orders.length && (
            <Table className='table mb-0'>
                <thead>
                    <tr>
                        <th>#</th>
                        <th className='text-muted text-center text-extra-small mb-2'>
                            Tên sản phẩm
                        </th>
                        <th className='text-muted text-center text-extra-small mb-2'>
                            Số lượng
                        </th>
                        <th className='text-center text-muted text-extra-small mb-2'>
                            Đơn giá
                        </th>
                        {/* <th className="text-right text-muted text-extra-small mb-2">
            Chi tiết
          </th> */}
                        <th className='text-right text-muted text-extra-small mb-2 cursor-pointer'>
                            Xóa
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {orders.map((order, idx) => (
                        <React.Fragment key={`${order.id}-${idx}`}>
                            <tr>
                                <td>{idx + 1}</td>
                                <td onClick={() => incrementItem(order)}>
                                    {order.name}
                                </td>
                                <td
                                    className='text-center'
                                    onClick={() => decrementItem(order)}
                                >
                                    {order.count}
                                </td>
                                <td className='text-center'>
                                    {AddCommaNumber(order.price)}
                                </td>
                                <td
                                    className='text-right cursor-pointer'
                                    onClick={() => onRemoveOrder(order)}
                                >
                                    <i className='simple-icon-trash' />
                                </td>
                            </tr>
                            <OrderDetailBadges order={order} menuItems={menuItems} />
                        </React.Fragment>
                    ))}
                </tbody>
            </Table>
        )
    );
};

const RenderModalContent = ({
    totalPrice,
    onToggleModal,
    orders,
    incrementItem,
    decrementItem,
    phone,
    name,
    deliveryTime,
    customerAddress,
    customerFormErrors = {},
    isTableOrder,
    companyName,
    onChange,
    onRemoveOrder,
    handleSubmit,
    menuItems,
}) => (
    <>
        <ModalHeader toggle={onToggleModal} className='border-bottom-0 p-3'>
            <IntlMessages id='pages.order-new-modal-title' />
        </ModalHeader>
        <ModalBody className='pad-model-order-preview table-right scroll'>
            <PerfectScrollbar
                options={{
                    suppressScrollX: true,
                    wheelPropagation: false,
                }}
                id='table-orders'
            >
                {renderListOrders({
                    orders,
                    incrementItem,
                    decrementItem,
                    onRemoveOrder,
                    menuItems,
                })}
                {orders.length ? (
                    <div className='px-3'>
                        <hr
                            className='my-2'
                            style={{ borderTop: '1px solid #e8e8e8' }}
                        />
                    </div>
                ) : null}
                <div className='p75 mt-2'>
                    <Label className='mt-2'>
                        <IntlMessages id='pages.total-order-price' />
                    </Label>
                    <span className='float-right'>
                        <strong>{AddCommaNumber(totalPrice)}</strong>
                    </span>
                </div>
            </PerfectScrollbar>
        </ModalBody>
        <ModalFooter className='d-flex flex-column p-3'>
            <Row className='mb-3'>
                <Colxx xxs='6' className='pr-0'>
                    <Input
                        onChange={onChange}
                        value={name}
                        placeholder='Tên khách hàng'
                        name='customer_name'
                        className='border-top-0 border-right-0 border-left-0 rounded-0 p-1'
                        invalid={Boolean(customerFormErrors.customer_name)}
                    />
                    {customerFormErrors.customer_name ? (
                        <small className='text-danger d-block mt-1'>
                            {customerFormErrors.customer_name}
                        </small>
                    ) : null}
                </Colxx>
                <Colxx xxs='6' className='pr-0'>
                    <Input
                        onChange={onChange}
                        value={phone}
                        placeholder='Số điện thoại'
                        name='customer_phone'
                        className='border-primary border-top-0 border-right-0 border-left-0 rounded-0 p-1'
                        invalid={Boolean(customerFormErrors.customer_phone)}
                    />
                    {customerFormErrors.customer_phone ? (
                        <small className='text-danger d-block mt-1'>
                            {customerFormErrors.customer_phone}
                        </small>
                    ) : null}
                </Colxx>
            </Row>
            {!isTableOrder ? (
                <Row
                    className='mb-3 no-gutters'
                    style={{ flexWrap: 'nowrap' }}
                >
                    <Colxx
                        xxs='12'
                        className='pr-2'
                        style={{ flex: '0 0 30%', maxWidth: '30%' }}
                    >
                        <Input
                            type='text'
                            onChange={(e) => {
                                const formatted = normalizeDeliveryTimeValue(
                                    e.target.value,
                                );
                                onChange({
                                    target: {
                                        name: 'customer_delivery_time',
                                        value: formatted,
                                    },
                                });
                            }}
                            value={normalizeDeliveryTimeValue(deliveryTime)}
                            name='customer_delivery_time'
                            className='border-top-0 border-right-0 border-left-0 rounded-0 p-1 w-100'
                            style={{ paddingLeft: 0 }}
                            maxLength='5'
                            inputMode='numeric'
                            placeholder='HH:mm'
                            invalid={Boolean(
                                customerFormErrors.customer_delivery_time,
                            )}
                        />
                        <small className='text-muted d-block mt-1'>Giờ giao</small>
                        {customerFormErrors.customer_delivery_time ? (
                            <small className='text-danger d-block mt-1'>
                                {customerFormErrors.customer_delivery_time}
                            </small>
                        ) : null}
                    </Colxx>
                    <Colxx
                        xxs='12'
                        className='pl-2 pr-0'
                        style={{ flex: '0 0 70%', maxWidth: '70%' }}
                    >
                        <Input
                            onChange={onChange}
                            value={customerAddress}
                            placeholder='Địa chỉ'
                            name='customer_address'
                            className='border-primary border-top-0 border-right-0 border-left-0 rounded-0 p-1 w-100'
                            invalid={Boolean(customerFormErrors.customer_address)}
                        />
                        {customerFormErrors.customer_address ? (
                            <small className='text-danger d-block mt-1'>
                                {customerFormErrors.customer_address}
                            </small>
                        ) : null}
                    </Colxx>
                </Row>
            ) : null}
            <Button className='w-100' color='primary' onClick={handleSubmit}>
                {'Đặt ngay !'}
            </Button>{' '}
        </ModalFooter>
    </>
);

const ProductOrderModal = ({
    isOpen,
    onToggleModal,
    orders,
    incrementItem,
    decrementItem,
    phone,
    name,
    deliveryTime,
    customerAddress,
    customerFormErrors,
    isTableOrder,
    companyName,
    onToggleNote,
    onChange,
    onRemoveOrder,
    handleSubmit,
    calTotalPrice,
    isFromMenu,
    menuItems,
}) => {
    let totalPrice = calTotalPrice(orders);

    return !isFromMenu ? (
        <Modal
            isOpen={isOpen}
            toggle={onToggleModal}
            wrapClassName='modal-right'
            backdrop={true}
        >
            <RenderModalContent
                totalPrice={totalPrice}
                orders={orders}
                incrementItem={incrementItem}
                decrementItem={decrementItem}
                phone={phone}
                name={name}
                deliveryTime={deliveryTime}
                customerAddress={customerAddress}
                customerFormErrors={customerFormErrors}
                isTableOrder={isTableOrder}
                companyName={companyName}
                onChange={onChange}
                onRemoveOrder={onRemoveOrder}
                onToggleModal={onToggleModal}
                handleSubmit={handleSubmit}
                menuItems={menuItems}
            />
        </Modal>
    ) : (
        document.getElementById('table-chairs') &&
            ReactDOM.createPortal(
                <div
                    style={{
                        position: 'relative',
                        zIndex: 1050,
                    }}
                    tabIndex={-1}
                >
                    <div className='modal-right modal-right-custom'>
                        <div
                            className={classnames({
                                'modal show d-block': isOpen,
                            })}
                            role='dialog'
                            tabIndex={-1}
                        >
                            <div
                                className='modal-dialog z-index-100'
                                role='document'
                            >
                                <div className='modal-content'>
                                    <RenderModalContent
                                        totalPrice={totalPrice}
                                        orders={orders}
                                        incrementItem={incrementItem}
                                        decrementItem={decrementItem}
                                        phone={phone}
                                        name={name}
                                        deliveryTime={deliveryTime}
                                        customerAddress={customerAddress}
                                        customerFormErrors={customerFormErrors}
                                        isTableOrder={isTableOrder}
                                        companyName={companyName}
                                        onChange={onChange}
                                        onRemoveOrder={onRemoveOrder}
                                        onToggleModal={onToggleModal}
                                        handleSubmit={handleSubmit}
                                        menuItems={menuItems}
                                    />
                                </div>
                            </div>
                            <div
                                onClick={onToggleModal}
                                className={classnames({
                                    'modal-backdrop fade show z-index-99':
                                        isOpen,
                                })}
                            />
                        </div>
                    </div>
                </div>,
                document.getElementById('table-chairs')
            )
    );
};

export default ProductOrderModal;
